import React, { useState, useEffect } from 'react';
import axios from 'axios';
import constant from '../constants/constant';
import { levelRewardsAPI } from '../utils/api';
import SvgaPlayer from '../components/SvgaPlayer';
import AssetPreview from '../components/AssetPreview';
import { 
  Coins, Heart, Save, RefreshCw, Layers, CheckCircle, Search, 
  Sparkles, Plus, Edit2, Trash2, X, Image as ImageIcon, Shield, Award, Upload 
} from 'lucide-react';

const LevelSettings = () => {
  const [mainView, setMainView] = useState('thresholds'); // 'thresholds' or 'tier_rewards'
  const [activeTab, setActiveTab] = useState('wealth'); // 'wealth' or 'charm'
  
  // Thresholds state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [wealthLevels, setWealthLevels] = useState([]);
  const [charmLevels, setCharmLevels] = useState([]);

  // Tier Rewards state
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [tierRewards, setTierRewards] = useState([]);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [modalSaving, setModalSaving] = useState(false);

  // File Uploading States
  const [uploadingFrame, setUploadingFrame] = useState(false);
  const [uploadingBubble, setUploadingBubble] = useState(false);

  // Form State for Tier Reward Rule Modal
  const [rewardForm, setRewardForm] = useState({
    id: '',
    reward_type: 'wealth',
    min_level: 1,
    max_level: 20,
    tier_name: '',
    frame_title: '',
    frame_image_url: '',
    bubble_title: '',
    bubble_image_url: '',
    valid_days: -1,
    is_active: true
  });

  const [toast, setToast] = useState({ message: '', type: '', visible: false });

  const showToast = (message, type = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast({ message: '', type: '', visible: false }), 4000);
  };

  useEffect(() => {
    fetchConfigs();
    fetchTierRewards();
  }, []);

  // -------------------------------------------------------------
  // 1️⃣ Level Thresholds Config Logic (1-100)
  // -------------------------------------------------------------
  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${constant.API_BASE_URL}/levels/config`);
      if (res.data && res.data.success && res.data.data) {
        const { wealth_levels, charm_levels } = res.data.data;
        setWealthLevels(ensure100Levels(wealth_levels || []));
        setCharmLevels(ensure100Levels(charm_levels || []));
      }
    } catch (err) {
      console.error("Error fetching level configs:", err);
      showToast("Failed to load level configs: " + (err.response?.data?.message || err.message), "error");
    } finally {
      setLoading(false);
    }
  };

  const ensure100Levels = (existingList) => {
    const map = new Map();
    existingList.forEach(item => map.set(Number(item.level), Number(item.exp_required)));

    const result = [];
    for (let l = 1; l <= 100; l++) {
      let exp = map.has(l) ? map.get(l) : 0;
      if (!map.has(l)) {
        if (l === 1) exp = 0;
        else if (l === 2) exp = 1000;
        else if (l === 3) exp = 5000;
        else if (l === 4) exp = 15000;
        else exp = Math.floor(35000 + Math.pow(l - 5, 2.1) * 20000);
      }
      result.push({ level: l, exp_required: exp });
    }
    return result;
  };

  const handleExpChange = (levelNum, value) => {
    const val = Math.max(0, parseInt(value) || 0);
    if (activeTab === 'wealth') {
      setWealthLevels(prev => prev.map(item => item.level === levelNum ? { ...item, exp_required: val } : item));
    } else {
      setCharmLevels(prev => prev.map(item => item.level === levelNum ? { ...item, exp_required: val } : item));
    }
  };

  const handleSaveThresholds = async () => {
    setSaving(true);
    try {
      const currentList = activeTab === 'wealth' ? wealthLevels : charmLevels;
      const token = sessionStorage.getItem('adminToken');
      const res = await axios.post(`${constant.API_BASE_URL}/levels/admin/config`, {
        type: activeTab,
        levels: currentList
      }, {
        headers: { Authorization: token ? `Bearer ${token}` : '' }
      });

      if (res.data && res.data.success) {
        showToast(`Successfully saved ${activeTab.toUpperCase()} Level Thresholds (1-100)!`);
      } else {
        showToast(res.data?.message || 'Error saving level config', 'error');
      }
    } catch (err) {
      console.error("Error saving level config:", err);
      showToast("Error saving levels: " + (err.response?.data?.message || err.message), 'error');
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------
  // 2️⃣ Level Tier Rewards Logic & File Upload
  // -------------------------------------------------------------
  const fetchTierRewards = async () => {
    setRewardsLoading(true);
    try {
      const res = await levelRewardsAPI.getLevelRewards();
      if (res.data && (res.data.success || res.data.status)) {
        setTierRewards(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching tier rewards:", err);
      showToast("Failed to load tier rewards: " + (err.response?.data?.message || err.message), "error");
    } finally {
      setRewardsLoading(false);
    }
  };

  // Helper to check if URL is SVGA animation
  const isSvgaUrl = (url) => {
    if (!url) return false;
    const cleanUrl = url.split('?')[0].split('#')[0];
    return cleanUrl.toLowerCase().endsWith('.svga');
  };

  // Handle direct file upload for frame or bubble image
  const handleFileUpload = async (file, assetType) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    if (assetType === 'frame') setUploadingFrame(true);
    if (assetType === 'bubble') setUploadingBubble(true);

    try {
      const res = await levelRewardsAPI.uploadRewardImage(formData);
      if (res.data && (res.data.success || res.data.status) && res.data.url) {
        const uploadedUrl = res.data.url;
        if (assetType === 'frame') {
          setRewardForm(prev => ({ ...prev, frame_image_url: uploadedUrl }));
        } else {
          setRewardForm(prev => ({ ...prev, bubble_image_url: uploadedUrl }));
        }
        showToast(`${assetType === 'frame' ? 'Avatar Frame' : 'Chat Bubble'} image uploaded successfully!`);
      } else {
        showToast("Failed to upload image: " + (res.data?.message || 'Unknown error'), "error");
      }
    } catch (err) {
      console.error("Upload error:", err);
      showToast("Error uploading file: " + (err.response?.data?.message || err.message), "error");
    } finally {
      if (assetType === 'frame') setUploadingFrame(false);
      if (assetType === 'bubble') setUploadingBubble(false);
    }
  };

  const handleOpenRewardModal = (rule = null) => {
    if (rule) {
      setSelectedRule(rule);
      const frame = rule.frame_item || {};
      const bubble = rule.bubble_item || {};
      setRewardForm({
        id: rule.id || '',
        reward_type: rule.reward_type || activeTab,
        min_level: rule.min_level || 1,
        max_level: rule.max_level || 20,
        tier_name: rule.tier_name || '',
        frame_title: frame.title || '',
        frame_image_url: frame.image_url || '',
        bubble_title: bubble.title || '',
        bubble_image_url: bubble.image_url || '',
        valid_days: rule.valid_days !== undefined ? rule.valid_days : -1,
        is_active: rule.is_active !== undefined ? rule.is_active : true
      });
    } else {
      setSelectedRule(null);
      setRewardForm({
        id: '',
        reward_type: activeTab,
        min_level: 1,
        max_level: 20,
        tier_name: `${activeTab.toUpperCase()} Tier (Lv 1-20)`,
        frame_title: `${activeTab === 'wealth' ? 'Wealth' : 'Charm'} Frame (1-20)`,
        frame_image_url: '',
        bubble_title: `${activeTab === 'wealth' ? 'Wealth' : 'Charm'} Bubble (1-20)`,
        bubble_image_url: '',
        valid_days: -1,
        is_active: true
      });
    }
    setShowRewardModal(true);
  };

  const handleSaveRewardModal = async (e) => {
    e.preventDefault();
    setModalSaving(true);
    try {
      const payload = {
        id: rewardForm.id || undefined,
        reward_type: rewardForm.reward_type,
        min_level: parseInt(rewardForm.min_level),
        max_level: parseInt(rewardForm.max_level),
        tier_name: rewardForm.tier_name,
        frame_item: {
          item_id: `frame_${rewardForm.reward_type}_${rewardForm.min_level}_${rewardForm.max_level}`,
          title: rewardForm.frame_title || `${rewardForm.reward_type.toUpperCase()} Frame`,
          image_url: rewardForm.frame_image_url,
          category: 'Frame'
        },
        bubble_item: {
          item_id: `bubble_${rewardForm.reward_type}_${rewardForm.min_level}_${rewardForm.max_level}`,
          title: rewardForm.bubble_title || `${rewardForm.reward_type.toUpperCase()} Bubble`,
          image_url: rewardForm.bubble_image_url,
          category: 'Bubble'
        },
        valid_days: parseInt(rewardForm.valid_days),
        is_active: rewardForm.is_active
      };

      const res = await levelRewardsAPI.createOrUpdateLevelReward(payload);
      if (res.data && (res.data.success || res.data.status)) {
        showToast("Level Tier Reward Rule saved successfully!");
        setShowRewardModal(false);
        fetchTierRewards();
      } else {
        showToast(res.data?.message || "Failed to save tier reward rule", "error");
      }
    } catch (err) {
      console.error("Error saving tier reward:", err);
      showToast("Error saving rule: " + (err.response?.data?.message || err.message), "error");
    } finally {
      setModalSaving(false);
    }
  };

  const handleDeleteReward = async (id) => {
    if (!window.confirm("Are you sure you want to delete this level tier reward rule?")) return;
    try {
      const res = await levelRewardsAPI.deleteLevelReward(id);
      if (res.data && (res.data.success || res.data.status)) {
        showToast("Tier reward rule deleted successfully!");
        fetchTierRewards();
      }
    } catch (err) {
      console.error("Error deleting tier reward:", err);
      showToast("Error deleting rule: " + (err.response?.data?.message || err.message), "error");
    }
  };

  const currentLevels = activeTab === 'wealth' ? wealthLevels : charmLevels;
  const filteredLevels = currentLevels.filter(item => 
    item.level.toString().includes(searchQuery) ||
    item.exp_required.toString().includes(searchQuery)
  );

  const filteredRewards = tierRewards.filter(r => r.reward_type === activeTab);

  return (
    <div className="space-y-6 animate-in fade-in duration-300 pb-12">
      {/* Top Main Navigation Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800 flex items-center gap-3">
            <Layers className="text-pink-600" size={32} /> Level Engine & Backpack Rewards
          </h2>
          <p className="text-slate-500 mt-1">
            Superadmin panel for EXP thresholds, Avatar Frames & Chat Bubble Auto-Reward Backpack Triggers.
          </p>
        </div>
        <div className="flex gap-3">
          {mainView === 'tier_rewards' && (
            <button
              onClick={() => handleOpenRewardModal()}
              className="flex items-center gap-2 bg-gradient-to-r from-pink-600 to-purple-600 text-white px-5 py-2.5 rounded-xl font-bold shadow-lg shadow-pink-500/30 hover:opacity-95 transition"
            >
              <Plus size={18} /> + Add Tier Reward Rule
            </button>
          )}
          <button
            onClick={() => { fetchConfigs(); fetchTierRewards(); }}
            disabled={loading || rewardsLoading}
            className="flex items-center gap-2 bg-white text-slate-700 px-5 py-2.5 rounded-xl font-semibold shadow-sm border border-slate-200 hover:bg-slate-50 transition"
          >
            <RefreshCw size={16} className={(loading || rewardsLoading) ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Main View Switcher Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          onClick={() => setMainView('thresholds')}
          className={`p-5 rounded-2xl border text-left transition-all flex items-center justify-between ${
            mainView === 'thresholds'
              ? 'bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white border-pink-500 shadow-xl ring-2 ring-pink-500/30'
              : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 shadow-sm'
          }`}
        >
          <div className="space-y-1">
            <div className="flex items-center gap-2 font-bold text-xs uppercase tracking-wider text-pink-400">
              <Layers size={16} /> Module 1
            </div>
            <div className="text-lg font-black">Level Threshold Settings (1-100)</div>
            <div className={`text-xs ${mainView === 'thresholds' ? 'text-slate-300' : 'text-slate-500'}`}>
              Configure cumulative coin EXP required for each level.
            </div>
          </div>
          <Award className={mainView === 'thresholds' ? 'text-amber-400' : 'text-slate-400'} size={32} />
        </button>

        <button
          onClick={() => setMainView('tier_rewards')}
          className={`p-5 rounded-2xl border text-left transition-all flex items-center justify-between ${
            mainView === 'tier_rewards'
              ? 'bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 text-white border-pink-500 shadow-xl ring-2 ring-pink-500/30'
              : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 shadow-sm'
          }`}
        >
          <div className="space-y-1">
            <div className="flex items-center gap-2 font-bold text-xs uppercase tracking-wider text-purple-400">
              <Sparkles size={16} /> Module 2
            </div>
            <div className="text-lg font-black">Level Tier Rewards (Backpack Auto-Award)</div>
            <div className={`text-xs ${mainView === 'tier_rewards' ? 'text-slate-300' : 'text-slate-500'}`}>
              Assign Avatar Frames & Chat Bubbles for Level Ranges (1-20, 21-40, etc.)
            </div>
          </div>
          <Sparkles className={mainView === 'tier_rewards' ? 'text-pink-400' : 'text-slate-400'} size={32} />
        </button>
      </div>

      {/* Sub-Tabs: Wealth vs Charm */}
      <div className="flex bg-slate-100 p-1.5 rounded-2xl max-w-md border border-slate-200 shadow-inner">
        <button
          onClick={() => setActiveTab('wealth')}
          className={`flex-1 flex items-center justify-center gap-2.5 py-3 px-4 rounded-xl font-extrabold text-sm transition-all ${
            activeTab === 'wealth'
              ? 'bg-gradient-to-r from-amber-500 to-yellow-600 text-white shadow-md'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Coins size={18} /> 🪙 Wealth Levels (Sending)
        </button>
        <button
          onClick={() => setActiveTab('charm')}
          className={`flex-1 flex items-center justify-center gap-2.5 py-3 px-4 rounded-xl font-extrabold text-sm transition-all ${
            activeTab === 'charm'
              ? 'bg-gradient-to-r from-pink-500 to-rose-600 text-white shadow-md'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Heart size={18} /> 💖 Charm Levels (Receiving)
        </button>
      </div>

      {/* ============================================================== */}
      {/* VIEW 1: LEVEL THRESHOLDS CONFIG EDITOR (1 TO 100)              */}
      {/* ============================================================== */}
      {mainView === 'thresholds' && (
        <div className="space-y-6">
          {/* Info Card */}
          <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white p-6 rounded-2xl shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border border-indigo-900/50">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-pink-400 font-bold text-xs uppercase tracking-wider">
                <Sparkles size={16} /> Real-Time Level Engine Sync
              </div>
              <h3 className="text-xl font-bold">
                Configuring {activeTab === 'wealth' ? '🪙 Wealth Level Thresholds' : '💖 Charm Level Thresholds'}
              </h3>
              <p className="text-slate-300 text-xs max-w-2xl leading-relaxed">
                Levels 1 to 100 require cumulative coin EXP thresholds. The Mobile App fetches these thresholds live via <code className="bg-white/10 px-1.5 py-0.5 rounded text-pink-300">/api/levels/config</code>.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-md px-4 py-3 rounded-xl border border-white/10 text-right">
              <div className="text-xs font-semibold text-slate-300">Total Configured Levels</div>
              <div className="text-2xl font-black text-amber-300">{currentLevels.length} Levels</div>
            </div>
          </div>

          {/* Filter / Search Bar */}
          <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 w-full max-w-md bg-slate-50 px-4 py-2.5 rounded-xl border border-slate-200">
              <Search className="text-slate-400" size={18} />
              <input
                type="text"
                placeholder="Search Level (e.g. 5, 25, 100) or EXP amount..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-transparent border-none outline-none text-slate-700 text-sm font-semibold placeholder-slate-400"
              />
            </div>
            <div className="text-xs font-bold text-slate-400">
              Showing {filteredLevels.length} of 100 Levels
            </div>
          </div>

          {/* Level Thresholds Table */}
          <div className="bg-white rounded-2xl shadow-xl shadow-slate-100/50 border border-slate-100 overflow-hidden">
            {loading ? (
              <div className="p-20 text-center text-slate-500 font-semibold flex items-center justify-center gap-3">
                <RefreshCw className="animate-spin text-pink-600" size={24} /> Loading Level Configurations...
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 text-slate-400 text-xs font-extrabold uppercase tracking-wider border-b border-slate-100">
                      <th className="p-5 w-32">Level #</th>
                      <th className="p-5">Required EXP / Coins (Cumulative)</th>
                      <th className="p-5">Formatted Display</th>
                      <th className="p-5 text-right">Preview Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {filteredLevels.map((item) => {
                      const numVal = Number(item.exp_required || 0);
                      let formatted = `${numVal.toLocaleString()} Coins`;
                      if (numVal >= 1000000000) formatted = `💎 ${(numVal / 1000000000).toFixed(1)}B Coins`;
                      else if (numVal >= 1000000) formatted = `💎 ${(numVal / 1000000).toFixed(1)}M Coins`;
                      else if (numVal >= 1000) formatted = `💎 ${(numVal / 1000).toFixed(0)}K Coins`;

                      return (
                        <tr key={item.level} className="hover:bg-slate-50/70 transition">
                          <td className="p-5">
                            <div className="flex items-center gap-2">
                              <span className={`w-8 h-8 rounded-xl flex items-center justify-center font-black text-xs ${
                                activeTab === 'wealth' 
                                  ? 'bg-amber-100 text-amber-800 border border-amber-200' 
                                  : 'bg-pink-100 text-pink-800 border border-pink-200'
                              }`}>
                                L{item.level}
                              </span>
                              <span className="font-bold text-slate-800">Level {item.level}</span>
                            </div>
                          </td>
                          <td className="p-5">
                            <div className="flex items-center gap-3">
                              <input
                                type="number"
                                min="0"
                                value={item.exp_required}
                                onChange={(e) => handleExpChange(item.level, e.target.value)}
                                className="w-full max-w-xs px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 focus:bg-white focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none transition"
                              />
                              <span className="text-xs font-semibold text-slate-400">Coins</span>
                            </div>
                          </td>
                          <td className="p-5">
                            <span className="font-extrabold text-slate-700 bg-slate-100 px-3 py-1 rounded-lg text-xs">
                              {formatted}
                            </span>
                          </td>
                          <td className="p-5 text-right">
                            {item.level === 1 ? (
                              <span className="text-xs font-bold text-green-600 bg-green-50 px-2.5 py-1 rounded-lg border border-green-100">
                                Starter Level (0 Coins)
                              </span>
                            ) : (
                              <span className="text-xs font-semibold text-slate-500">
                                Requires {numVal.toLocaleString()} Coins
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Floating Save Bar */}
          <div className="sticky bottom-6 bg-slate-900/90 backdrop-blur-md text-white p-4 rounded-2xl shadow-2xl border border-slate-800 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="text-green-400" size={24} />
              <div>
                <div className="font-bold text-sm">Ready to save Level Configurations?</div>
                <div className="text-xs text-slate-400">Updates will reflect live across the Mobile App and User Profile API.</div>
              </div>
            </div>
            <button
              onClick={handleSaveThresholds}
              disabled={saving}
              className="flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-6 py-2.5 rounded-xl font-extrabold shadow-lg shadow-pink-500/40 transition"
            >
              <Save size={18} /> Save {activeTab.toUpperCase()} Thresholds
            </button>
          </div>
        </div>
      )}

      {/* ============================================================== */}
      {/* VIEW 2: LEVEL TIER REWARDS MODULE (AVATAR FRAMES & BUBBLES)    */}
      {/* ============================================================== */}
      {mainView === 'tier_rewards' && (
        <div className="space-y-6">
          {/* Info Header Banner */}
          <div className="bg-gradient-to-r from-purple-900 via-indigo-950 to-purple-900 text-white p-6 rounded-2xl shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border border-purple-800/50">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-pink-400 font-bold text-xs uppercase tracking-wider">
                <Sparkles size={16} /> Level-Up Backpack Auto-Trigger Engine
              </div>
              <h3 className="text-xl font-bold">
                {activeTab === 'wealth' ? '🪙 Wealth Level Tier Rewards' : '💖 Charm Level Tier Rewards'}
              </h3>
              <p className="text-slate-300 text-xs max-w-3xl leading-relaxed">
                When a user reaches a level range (e.g. Lv 1-20, 21-40), the backend automatically inserts the assigned Avatar Frame & Chat Bubble into their Backpack with <code className="bg-white/10 px-1.5 py-0.5 rounded text-pink-300">source: "level_reward"</code>.
              </p>
            </div>
            <button
              onClick={() => handleOpenRewardModal()}
              className="bg-pink-600 hover:bg-pink-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-pink-500/30 flex items-center gap-2 whitespace-nowrap transition"
            >
              <Plus size={16} /> Add New Tier Rule
            </button>
          </div>

          {/* Tier Rules Cards List */}
          {rewardsLoading ? (
            <div className="p-20 bg-white rounded-2xl text-center text-slate-500 font-semibold flex items-center justify-center gap-3 border border-slate-100">
              <RefreshCw className="animate-spin text-pink-600" size={24} /> Loading Level Tier Rewards...
            </div>
          ) : filteredRewards.length === 0 ? (
            <div className="p-16 bg-white rounded-2xl text-center space-y-4 border border-slate-200">
              <div className="w-16 h-16 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center mx-auto">
                <Sparkles size={32} />
              </div>
              <h3 className="text-lg font-bold text-slate-800">No Tier Rules Configured for {activeTab.toUpperCase()}</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">
                Click below to set up Avatar Frames & Chat Bubbles for Level ranges (e.g., Lv 1-20, 21-40).
              </p>
              <button
                onClick={() => handleOpenRewardModal()}
                className="bg-pink-600 text-white px-6 py-2.5 rounded-xl font-bold shadow-lg shadow-pink-500/30 hover:bg-pink-700 transition"
              >
                + Create First {activeTab.toUpperCase()} Tier Rule
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {filteredRewards.map((rule) => {
                const frame = rule.frame_item || {};
                const bubble = rule.bubble_item || {};

                return (
                  <div key={rule.id} className="bg-white rounded-2xl p-6 shadow-xl shadow-slate-100/50 border border-slate-100 space-y-6 hover:border-pink-200 transition">
                    {/* Header Row */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider ${
                          activeTab === 'wealth'
                            ? 'bg-amber-100 text-amber-800 border border-amber-200'
                            : 'bg-pink-100 text-pink-800 border border-pink-200'
                        }`}>
                          Level {rule.min_level} - {rule.max_level}
                        </span>
                        <div>
                          <h4 className="text-lg font-black text-slate-800">{rule.tier_name || `Tier (Lv ${rule.min_level}-${rule.max_level})`}</h4>
                          <span className="text-xs text-slate-400 font-semibold">Rule ID: {rule.id}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          rule.valid_days === -1 ? 'bg-indigo-100 text-indigo-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {rule.valid_days === -1 ? '♾️ Permanent Reward' : `⏳ ${rule.valid_days} Days Valid`}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          rule.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {rule.is_active ? 'Active' : 'Disabled'}
                        </span>
                        <button
                          onClick={() => handleOpenRewardModal(rule)}
                          className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition"
                          title="Edit Rule"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteReward(rule.id)}
                          className="p-2 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-xl transition"
                          title="Delete Rule"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Reward Assets Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Avatar Frame Box */}
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex items-center gap-4">
                        <div className="w-16 h-16 bg-slate-200/70 rounded-xl flex items-center justify-center overflow-hidden border border-slate-300 relative">
                          {frame.image_url ? (
                            <AssetPreview src={frame.image_url} alt={frame.title} className="w-full h-full object-contain p-1" />
                          ) : (
                            <ImageIcon className="text-slate-400" size={24} />
                          )}
                        </div>
                        <div className="space-y-1 flex-1">
                          <div className="text-xs font-bold text-pink-600 uppercase tracking-wider flex items-center gap-1">
                            <Shield size={14} /> Avatar Frame Item
                          </div>
                          <div className="font-extrabold text-slate-800 text-sm">{frame.title || 'No Frame Title'}</div>
                          <div className="text-xs text-slate-400 truncate max-w-xs">{frame.image_url || 'No Image URL'}</div>
                        </div>
                      </div>

                      {/* Chat Bubble Box */}
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex items-center gap-4">
                        <div className="w-16 h-16 bg-slate-200/70 rounded-xl flex items-center justify-center overflow-hidden border border-slate-300 relative">
                          {bubble.image_url ? (
                            <AssetPreview src={bubble.image_url} alt={bubble.title} className="w-full h-full object-contain p-1" />
                          ) : (
                            <ImageIcon className="text-slate-400" size={24} />
                          )}
                        </div>
                        <div className="space-y-1 flex-1">
                          <div className="text-xs font-bold text-purple-600 uppercase tracking-wider flex items-center gap-1">
                            <Sparkles size={14} /> Chat Bubble Item
                          </div>
                          <div className="font-extrabold text-slate-800 text-sm">{bubble.title || 'No Bubble Title'}</div>
                          <div className="text-xs text-slate-400 truncate max-w-xs">{bubble.image_url || 'No Image URL'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ============================================================== */}
      {/* MODAL: ADD / EDIT TIER REWARD RULE                             */}
      {/* ============================================================== */}
      {showRewardModal && (
        <div className="fixed inset-0 z-[99999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-8 shadow-2xl border border-slate-100 space-y-6 animate-in zoom-in-95 duration-200 my-8">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div>
                <h3 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">
                  <Sparkles className="text-pink-600" size={24} /> 
                  {selectedRule ? 'Edit Tier Reward Rule' : `Add New ${activeTab.toUpperCase()} Tier Reward Rule`}
                </h3>
                <p className="text-xs text-slate-500 mt-1">Configure level range and upload/assign Avatar Frame & Chat Bubble assets.</p>
              </div>
              <button 
                onClick={() => setShowRewardModal(false)}
                className="p-2 hover:bg-slate-100 text-slate-400 hover:text-slate-700 rounded-full transition"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveRewardModal} className="space-y-6">
              {/* Reward Type Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Reward Type</label>
                  <select
                    value={rewardForm.reward_type}
                    onChange={(e) => setRewardForm({ ...rewardForm, reward_type: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 outline-none focus:border-pink-500"
                  >
                    <option value="wealth">🪙 Wealth Level Reward (Sending)</option>
                    <option value="charm">💖 Charm Level Reward (Receiving)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Tier Display Title</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Bronze Tier (Lv 1-20)"
                    value={rewardForm.tier_name}
                    onChange={(e) => setRewardForm({ ...rewardForm, tier_name: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-semibold text-slate-800 outline-none focus:border-pink-500"
                  />
                </div>
              </div>

              {/* Level Range Inputs */}
              <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-200">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Min Level Range</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    required
                    value={rewardForm.min_level}
                    onChange={(e) => setRewardForm({ ...rewardForm, min_level: e.target.value })}
                    className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl font-bold text-slate-800 outline-none focus:border-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Max Level Range</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    required
                    value={rewardForm.max_level}
                    onChange={(e) => setRewardForm({ ...rewardForm, max_level: e.target.value })}
                    className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl font-bold text-slate-800 outline-none focus:border-pink-500"
                  />
                </div>
              </div>

              {/* Avatar Frame Inputs + Direct File Upload */}
              <div className="bg-pink-50/50 p-5 rounded-2xl border border-pink-100 space-y-4">
                <div className="text-xs font-extrabold text-pink-700 uppercase tracking-wider flex items-center justify-between">
                  <span className="flex items-center gap-1.5"><Shield size={16} /> 1. Avatar Frame Reward Asset</span>
                  {rewardForm.frame_image_url && (
                    <span className="text-xs text-green-600 font-bold bg-green-50 px-2 py-0.5 rounded border border-green-200">✓ Image Attached</span>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Frame Title</label>
                    <input
                      type="text"
                      placeholder="e.g. Bronze Crown Frame"
                      value={rewardForm.frame_title}
                      onChange={(e) => setRewardForm({ ...rewardForm, frame_title: e.target.value })}
                      className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl font-semibold text-slate-800 outline-none focus:border-pink-500 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Upload Frame File (.png, .gif, .svga) / URL</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="https://.../frame.png or upload below"
                        value={rewardForm.frame_image_url}
                        onChange={(e) => setRewardForm({ ...rewardForm, frame_image_url: e.target.value })}
                        className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl font-semibold text-slate-800 outline-none focus:border-pink-500 text-sm"
                      />
                      <label className={`px-4 py-2 rounded-xl font-bold text-xs whitespace-nowrap cursor-pointer flex items-center gap-1.5 transition ${
                        uploadingFrame ? 'bg-slate-300 text-slate-600' : 'bg-pink-600 text-white hover:bg-pink-700 shadow-md shadow-pink-500/20'
                      }`}>
                        <Upload size={14} className={uploadingFrame ? 'animate-bounce' : ''} />
                        {uploadingFrame ? 'Uploading...' : 'Browse'}
                        <input
                          type="file"
                          accept="image/*,.png,.svg,.gif,.jpg,.webp,.svga"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e.target.files[0], 'frame')}
                          disabled={uploadingFrame}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Frame Preview Thumbnail */}
                {rewardForm.frame_image_url && (
                  <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-pink-200">
                    <div className="w-12 h-12 rounded-lg bg-slate-100 overflow-hidden border border-slate-200 flex items-center justify-center">
                      <AssetPreview src={rewardForm.frame_image_url} alt="Frame Preview" className="w-full h-full object-contain p-0.5" />
                    </div>
                    <div className="text-xs space-y-0.5 truncate flex-1">
                      <div className="font-bold text-slate-800">Avatar Frame Live Preview</div>
                      <div className="text-slate-400 truncate">{rewardForm.frame_image_url}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Chat Bubble Inputs + Direct File Upload */}
              <div className="bg-purple-50/50 p-5 rounded-2xl border border-purple-100 space-y-4">
                <div className="text-xs font-extrabold text-purple-700 uppercase tracking-wider flex items-center justify-between">
                  <span className="flex items-center gap-1.5"><Sparkles size={16} /> 2. Chat Bubble Reward Asset</span>
                  {rewardForm.bubble_image_url && (
                    <span className="text-xs text-green-600 font-bold bg-green-50 px-2 py-0.5 rounded border border-green-200">✓ Image Attached</span>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Bubble Title</label>
                    <input
                      type="text"
                      placeholder="e.g. Bronze Sparkle Bubble"
                      value={rewardForm.bubble_title}
                      onChange={(e) => setRewardForm({ ...rewardForm, bubble_title: e.target.value })}
                      className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl font-semibold text-slate-800 outline-none focus:border-purple-500 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Upload Bubble File (.png, .gif, .svga) / URL</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="https://.../bubble.png or upload below"
                        value={rewardForm.bubble_image_url}
                        onChange={(e) => setRewardForm({ ...rewardForm, bubble_image_url: e.target.value })}
                        className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl font-semibold text-slate-800 outline-none focus:border-purple-500 text-sm"
                      />
                      <label className={`px-4 py-2 rounded-xl font-bold text-xs whitespace-nowrap cursor-pointer flex items-center gap-1.5 transition ${
                        uploadingBubble ? 'bg-slate-300 text-slate-600' : 'bg-purple-600 text-white hover:bg-purple-700 shadow-md shadow-purple-500/20'
                      }`}>
                        <Upload size={14} className={uploadingBubble ? 'animate-bounce' : ''} />
                        {uploadingBubble ? 'Uploading...' : 'Browse'}
                        <input
                          type="file"
                          accept="image/*,.png,.svg,.gif,.jpg,.webp,.svga"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e.target.files[0], 'bubble')}
                          disabled={uploadingBubble}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Bubble Preview Thumbnail */}
                {rewardForm.bubble_image_url && (
                  <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-purple-200">
                    <div className="w-12 h-12 rounded-lg bg-slate-100 overflow-hidden border border-slate-200 flex items-center justify-center">
                      <AssetPreview src={rewardForm.bubble_image_url} alt="Bubble Preview" className="w-full h-full object-contain p-0.5" />
                    </div>
                    <div className="text-xs space-y-0.5 truncate flex-1">
                      <div className="font-bold text-slate-800">Chat Bubble Live Preview</div>
                      <div className="text-slate-400 truncate">{rewardForm.bubble_image_url}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Expiry & Status Options */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Valid Duration</label>
                  <select
                    value={rewardForm.valid_days}
                    onChange={(e) => setRewardForm({ ...rewardForm, valid_days: parseInt(e.target.value) })}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 outline-none focus:border-pink-500 text-sm"
                  >
                    <option value="-1">♾️ Permanent (-1 Lifetime)</option>
                    <option value="7">⏳ 7 Days</option>
                    <option value="30">⏳ 30 Days</option>
                    <option value="90">⏳ 90 Days</option>
                    <option value="365">⏳ 365 Days</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Rule Status</label>
                  <select
                    value={rewardForm.is_active ? "true" : "false"}
                    onChange={(e) => setRewardForm({ ...rewardForm, is_active: e.target.value === "true" })}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 outline-none focus:border-pink-500 text-sm"
                  >
                    <option value="true">✅ Active & Auto-Trigger Enabled</option>
                    <option value="false">⛔ Disabled</option>
                  </select>
                </div>
              </div>

              {/* Modal Buttons */}
              <div className="flex items-center justify-end gap-3 border-t border-slate-100 pt-4">
                <button
                  type="button"
                  onClick={() => setShowRewardModal(false)}
                  className="px-5 py-2.5 rounded-xl font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={modalSaving || uploadingFrame || uploadingBubble}
                  className="px-6 py-2.5 rounded-xl font-extrabold text-white bg-pink-600 hover:bg-pink-700 shadow-lg shadow-pink-500/30 transition flex items-center gap-2"
                >
                  <Save size={18} /> {modalSaving ? 'Saving...' : 'Save Tier Rule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed bottom-6 right-6 z-[99999] px-6 py-3.5 rounded-2xl shadow-2xl border text-sm font-bold flex items-center gap-3 animate-in slide-in-from-bottom-5 fade-in duration-300 ${
          toast.type === 'error' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-slate-900 text-green-400 border-slate-800'
        }`}>
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
};

export default LevelSettings;
